import { request } from '@/lib/api'

/** Уведомление в центре уведомлений. */
export interface Notification {
  id: string
  title: string
  body: string | null
  kind: string
  entityType: string | null
  entityId: string | null
  read: boolean
  createdAt: string
}

export interface NotificationFeed {
  notifications: Notification[]
  unread: number
}

/** Комментарий; ответы несут `parentId`, дерево строит клиент. */
export interface Comment {
  id: string
  entityType: string
  entityId: string
  parentId: string | null
  body: string
  mentionedUserIds: string[]
  createdBy: string | null
  createdAt: string
}

export function fetchNotifications(
  unreadOnly = false,
  signal?: AbortSignal,
): Promise<NotificationFeed> {
  return request<NotificationFeed>(
    `/notifications?unreadOnly=${unreadOnly}`,
    { signal },
  )
}

export function markNotificationRead(id: string): Promise<{ unread: number }> {
  return request<{ unread: number }>(`/notifications/${id}/read`, { method: 'POST' })
}

export function markAllNotificationsRead(): Promise<{ marked: number; unread: number }> {
  return request<{ marked: number; unread: number }>('/notifications/read-all', {
    method: 'POST',
  })
}

/**
 * Оставляет комментарий.
 *
 * `mentionedUserIds` — идентификаторы, которые подставил интерфейс, а не результат
 * разбора текста. Сервер их сверяет: несуществующий или отключённый пользователь —
 * ошибка, а не тихо пропущенный получатель.
 */
export function postComment(
  entityType: string,
  entityId: string,
  body: string,
  options: { parentId?: string; mentionedUserIds?: string[] } = {},
): Promise<Comment> {
  return request<Comment>(`/entities/${entityType}/${entityId}/comments`, {
    method: 'POST',
    body: {
      body,
      parentId: options.parentId ?? null,
      mentionedUserIds: options.mentionedUserIds ?? [],
    },
  })
}

/** Кого поле комментария может упомянуть (#114). */
export interface Mentionable {
  id: string
  name: string
  username: string
}

/**
 * Коллеги по подстроке имени или логина. Учётные записи клиентов сервер не предлагает и
 * при отправке отвергает: внутреннее обсуждение клиенту не пересылается.
 */
function fetchMentionable(query: string, signal?: AbortSignal): Promise<{ users: Mentionable[] }> {
  return request<{ users: Mentionable[] }>(
    `/comments/mentionable?q=${encodeURIComponent(query)}&limit=8`,
    { signal },
  )
}

/** Поиск для поля комментария: имя на кнопке, идентификатор — на сервер. */
export function searchMentionable(query: string, signal?: AbortSignal): Promise<{ id: string; name: string }[]> {
  return fetchMentionable(query, signal).then((found) =>
    found.users.map((user) => ({ id: user.id, name: user.name || user.username })),
  )
}
